import {
  ContentBlock,
  ContentState,
  EditorState,
  convertFromRaw,
  RawDraftContentBlock,
  RawDraftEntity,
  RawDraftEntityRange
} from "draft-js";
import { v4 as uuid } from "uuid";

import { CampaignWithCalleeFields_calleeFields } from "src/graphql/generated/CampaignWithCalleeFields";
import { CalleeField } from "src/graphql/generated/globalTypes";

import decorator from "./decorator";
import { convertAnyCalleeFieldToBaseEntity } from "./richTextConverters";

const calleeFieldRegex = /{callee_field:([a-z0-9-]*|built_in:[A-Za-z0-9_-]*)}/;

export type CalleeFieldMatchResult =
  | { match: false; placeholder: string }
  | { match: true; calleeField: CampaignWithCalleeFields_calleeFields };

export type CalleeFieldEntityData =
  | {
      calleeFieldId: string;
    }
  | { builtInField: CalleeField }
  | { name?: string }
  | { isInvalid: true };

export function entityDataForCalleeField(
  calleeField: CampaignWithCalleeFields_calleeFields
): CalleeFieldEntityData {
  if (calleeField.id) {
    return { calleeFieldId: calleeField.id };
  } else if (calleeField.builtInField) {
    return { builtInField: calleeField.builtInField };
  } else {
    throw new Error(
      `Callee field ${JSON.stringify(
        calleeField
      )} has no ID or built-in field name`
    );
  }
}

function serializeCalleeFieldEntityDataForNonRichTextScript(
  entityData: CalleeFieldEntityData
): string {
  if ("calleeFieldId" in entityData && entityData.calleeFieldId) {
    return `{callee_field:${entityData.calleeFieldId}}`;
  } else if ("builtInField" in entityData && entityData.builtInField) {
    return `{callee_field:built_in:${entityData.builtInField}}`;
    // if a callee field is invalid, remove entirely so that it is not included in a text message
  } else if ("isInvalid" in entityData && entityData.isInvalid) {
    return "";
  } else if ("name" in entityData) {
    return entityData.name || "(missing field)";
  } else {
    throw new Error(
      `Callee field entity data ${JSON.stringify(
        entityData
      )} has no ID or built-in field name`
    );
  }
}

/**
 * Helper to count the number of unicode code points in a string.
 *
 * This is required because in `deserializePlainText` below, we deserialize the script
 * into a plain-text script plus "entity ranges" to highlight callee fields.
 *
 * DraftJS entity ranges are specified as unicode codepoint offsets, but
 * Javascript string processing uses indexes into the UTF-16-encoded strings.
 * So this function converts from Javascript string indexes to DraftJS string
 * indexes.
 */
function unicodeCharacterLength(str: string): number {
  return Array.from(str).length;
}

function defaultCalleeFieldMatcher(
  fieldId: string,
  calleeFields: CampaignWithCalleeFields_calleeFields[]
): CalleeFieldMatchResult {
  if (fieldId.startsWith("built_in:")) {
    // The "built_in:" notation is only used for *unsaved* built-in fields (ones
    // that haven't been assigned an ID for this campaign yet). These typically
    // don't appear when deserializing data -- if we got some data from the
    // server, then all fields in that data will have IDs.
    //
    // We may do some matching in other context that aren't dealing with server-
    // side data, such as copy-paste.
    return { match: false, placeholder: "" };
  }

  const match = calleeFields.find(field => field.id === fieldId);
  if (match) {
    return { match: true, calleeField: match };
  }
  return { match: false, placeholder: "" };
}

export function deserializePlainText(
  scriptString: string,
  calleeFields: CampaignWithCalleeFields_calleeFields[],
  { calleeFieldMatcher = defaultCalleeFieldMatcher } = {}
): ContentState {
  const entityMap: { [key: number]: RawDraftEntity } = {};
  const lines = scriptString.split("\n");
  const blocks: RawDraftContentBlock[] = [];
  let currentEntityKey = 0;
  for (const line of lines) {
    let transformedLine = line;
    let calleeFieldMatch = calleeFieldRegex.exec(transformedLine);
    const entityRanges: RawDraftEntityRange[] = [];
    while (calleeFieldMatch) {
      const matchingString = calleeFieldMatch[0];
      const matchStart = calleeFieldMatch.index;
      const matchEnd = matchStart + matchingString.length;
      const fieldId = matchingString.substring(
        "{callee_field:".length,
        matchingString.length - "}".length
      );
      const calleeFieldMatchResult = calleeFieldMatcher(fieldId, calleeFields);

      if (calleeFieldMatchResult.match) {
        transformedLine =
          transformedLine.slice(0, matchStart) +
          calleeFieldMatchResult.calleeField.name +
          transformedLine.slice(matchEnd);
        entityRanges.push({
          key: currentEntityKey,
          offset: unicodeCharacterLength(transformedLine.slice(0, matchStart)),
          length: unicodeCharacterLength(
            calleeFieldMatchResult.calleeField.name
          )
        });

        entityMap[currentEntityKey] = {
          type: "CALLEE_FIELD",
          mutability: "IMMUTABLE",
          data: entityDataForCalleeField(calleeFieldMatchResult.calleeField)
        };
        currentEntityKey++;
      } else {
        transformedLine =
          transformedLine.slice(0, matchStart) +
          calleeFieldMatchResult.placeholder +
          transformedLine.slice(matchEnd);

        entityRanges.push({
          key: currentEntityKey,
          offset: unicodeCharacterLength(transformedLine.slice(0, matchStart)),
          length: unicodeCharacterLength(calleeFieldMatchResult.placeholder)
        });

        entityMap[currentEntityKey] = {
          type: "CALLEE_FIELD",
          mutability: "IMMUTABLE",
          data: {
            name: calleeFieldMatchResult.placeholder,
            isInvalid: true
          }
        };
        currentEntityKey++;
      }

      calleeFieldMatch = calleeFieldRegex.exec(transformedLine);
    }
    blocks.push({
      key: uuid(),
      type: "unstyled",
      depth: 1,
      inlineStyleRanges: [],
      text: transformedLine,
      entityRanges
    });
  }
  return convertFromRaw({ blocks, entityMap });
}

export function plainTextToInitialEditorState(
  plaintextScript: string,
  calleeFields: CampaignWithCalleeFields_calleeFields[],
  isRichText: boolean | null = false
): EditorState {
  const scriptContentState = !isRichText
    ? deserializePlainText(plaintextScript, calleeFields)
    : convertFromRaw(JSON.parse(plaintextScript));
  return EditorState.createWithContent(scriptContentState, decorator);
}

export function emptyEditorState(): EditorState {
  return EditorState.createEmpty(decorator);
}

function serializeBlock(
  contentBlock: ContentBlock,
  contentState: ContentState
) {
  let calleeFieldData: CalleeFieldEntityData | null = null;
  const entities: {
    calleeFieldData: CalleeFieldEntityData;
    start: number;
    end: number;
  }[] = [];
  contentBlock.findEntityRanges(
    character => {
      const entityKey = character.getEntity();
      if (
        entityKey &&
        contentState.getEntity(entityKey) &&
        contentState.getEntity(entityKey).getType() === "CALLEE_FIELD"
      ) {
        calleeFieldData = contentState.getEntity(entityKey).getData();
        return true;
      }
      return false;
    },
    (start, end) => {
      entities.push({ calleeFieldData: calleeFieldData!, start, end });
    }
  );

  const blockText = contentBlock.getText();
  const outputSegments: string[] = [];
  outputSegments.push(
    blockText.slice(0, entities.length ? entities[0].start : blockText.length)
  );
  entities.forEach((entity, idx) => {
    const calleeFieldSegment =
      serializeCalleeFieldEntityDataForNonRichTextScript(
        convertAnyCalleeFieldToBaseEntity(entity.calleeFieldData)
      );
    outputSegments.push(calleeFieldSegment);
    const nextEntityStart =
      entities.length > idx + 1 ? entities[idx + 1].start : blockText.length;
    outputSegments.push(blockText.slice(entity.end, nextEntityStart));
  });
  return outputSegments.join("");
}

export function serialize(scriptContentState: ContentState): string {
  return scriptContentState
    .getBlocksAsArray()
    .map(block => serializeBlock(block, scriptContentState))
    .join("\n");
}

export function editorStateToPlainText(editorState: EditorState): string {
  return serialize(editorState.getCurrentContent());
}
